import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoadPanel } from "devextreme-react";
import notify from "devextreme/ui/notify";
import { Ticket, getTickets, isClosedStatus } from "../../../api/support";
import {
    getSlaInfo, formatSlaRemaining, STATUS_BADGE, STATUS_TEXT_KEYS,
} from "../messages/ticket-constants";
import type { TicketStatus } from "../../../api/support";

// SLA raporu: acik Ticketlerin due_date'inden turetilen SLA durumunu ozetler (6500 + getSlaInfo).
// Backend SLA servisi gelince metrikler gercek hedeflere baglanabilir.
export default function SlaReportPage() {
    const { t } = useTranslation();
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        getTickets()
            .then(setTickets)
            .catch(() => notify(t("ticketsLoadError"), "error", 2000))
            .finally(() => setLoading(false));
    }, [t]);

    const stats = useMemo(() => {
        let ontrack = 0, risk = 0, overdue = 0;
        const flagged: { ticket: Ticket; state: string; color: string; label: string; remainingMs: number }[] = [];
        for (const tk of tickets) {
            const sla = getSlaInfo(tk);
            if (sla.state === "none") continue;
            if (sla.state === "ontrack") ontrack++;
            else if (sla.state === "risk") risk++;
            else if (sla.state === "overdue") overdue++;
            if (sla.state === "risk" || sla.state === "overdue") {
                const remaining = formatSlaRemaining(sla.remainingMs as number, t);
                flagged.push({
                    ticket: tk,
                    state: sla.state,
                    color: sla.color,
                    label: sla.state === "overdue" ? t("slaOverdueBy", { time: remaining }) : t("slaRemaining", { time: remaining }),
                    remainingMs: sla.remainingMs as number,
                });
            }
        }
        const tracked = ontrack + risk + overdue;
        const compliance = tracked > 0 ? Math.round((ontrack / tracked) * 100) : 100;
        flagged.sort((a, b) => a.remainingMs - b.remainingMs); // en cok gecikmis en ustte

        // Ortalama cozum suresi: kapali Ticketlerde closed_at - created_at (gun).
        let sumDays = 0, closedCount = 0;
        for (const tk of tickets) {
            if (isClosedStatus(tk.status) && tk.closed_at && tk.created_at) {
                const d = (new Date(tk.closed_at).getTime() - new Date(tk.created_at).getTime()) / 86400000;
                if (d >= 0) { sumDays += d; closedCount++; }
            }
        }
        const avgResolutionDays = closedCount > 0 ? (sumDays / closedCount).toFixed(1) : null;

        return { ontrack, risk, overdue, tracked, compliance, flagged, avgResolutionDays };
    }, [tickets, t]);

    // Dagilim cubugu segmentleri
    const dist = [
        { label: t("slaOnTrack"), value: stats.ontrack, color: "#10B981" },
        { label: t("slaAtRisk"), value: stats.risk, color: "#F97316" },
        { label: t("slaOverdue"), value: stats.overdue, color: "#EF4444" },
    ];

    return (
        <div className="h-full w-full" style={{ overflowY: "auto", padding: 20, color: "var(--base-text-color)" }}>
            <LoadPanel visible={loading} />

            <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>{t("slaReportTitle")}</h2>

            {/* Ozet kartlar */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" style={{ marginBottom: 20 }}>
                <Metric label={t("slaCompliance")} value={`%${stats.compliance}`} subtitle={`${stats.ontrack} / ${stats.tracked}`} color="#6366F1" />
                <Metric label={t("slaOnTrack")} value={stats.ontrack} color="#10B981" />
                <Metric label={t("slaAtRisk")} value={stats.risk} color="#F97316" />
                <Metric label={t("slaOverdue")} value={stats.overdue} color="#EF4444" />
                <Metric label={t("avgResolutionTime")} value={stats.avgResolutionDays ? `${stats.avgResolutionDays} ${t("daysShort")}` : "—"} color="#0EA5E9" />
            </div>

            {/* SLA dagilim cubugu */}
            <div className="text-sm font-semibold uppercase" style={{ letterSpacing: "0.04em", color: "var(--subtitle-text-color)", marginBottom: 8 }}>
                {t("slaDistribution")}
            </div>
            <div style={{ background: "var(--base-bg)", border: "1px solid var(--border-color)", borderRadius: 10, padding: 16, marginBottom: 20 }}>
                <div style={{ display: "flex", height: 14, borderRadius: 999, overflow: "hidden", background: "var(--base-bg-darken-4)" }}>
                    {stats.tracked > 0 ? dist.map(d => d.value > 0 && (
                        <div key={d.label} title={`${d.label}: ${d.value}`} style={{ width: `${(d.value / stats.tracked) * 100}%`, background: d.color }} />
                    )) : null}
                </div>
                <div style={{ display: "flex", gap: 16, marginTop: 10, flexWrap: "wrap" }}>
                    {dist.map(d => (
                        <div key={d.label} className="flex items-center gap-2 text-xs" style={{ color: "var(--subtitle-text-color)" }}>
                            <span style={{ width: 10, height: 10, borderRadius: 3, background: d.color }} />
                            {d.label} <strong style={{ color: "var(--base-text-color)" }}>{d.value}</strong>
                        </div>
                    ))}
                </div>
            </div>

            {/* Risk & gecikmis liste */}
            <div className="text-sm font-semibold uppercase" style={{ letterSpacing: "0.04em", color: "var(--subtitle-text-color)", marginBottom: 8 }}>
                {t("slaAtRiskList")}
            </div>
            <div style={{ background: "var(--base-bg)", border: "1px solid var(--border-color)", borderRadius: 10, overflow: "hidden" }}>
                {stats.flagged.length === 0 ? (
                    <div className="text-sm" style={{ padding: 16, color: "var(--subtitle-text-color)" }}>{t("noSlaTickets")}</div>
                ) : (
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                            <tr style={{ textAlign: "left", color: "var(--subtitle-text-color)", fontSize: 12 }}>
                                <th style={thStyle}>{t("ticketNo")}</th>
                                <th style={thStyle}>{t("title")}</th>
                                <th style={thStyle}>{t("customer")}</th>
                                <th style={thStyle}>{t("assignedTo")}</th>
                                <th style={thStyle}>{t("supportGroup")}</th>
                                <th style={thStyle}>{t("status")}</th>
                                <th style={thStyle}>{t("sla")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {stats.flagged.map(f => {
                                const sc = STATUS_BADGE[f.ticket.status as TicketStatus];
                                return (
                                    <tr key={f.ticket.ref} style={{ borderTop: "1px solid var(--border-color)" }}>
                                        <td style={tdStyle}><span className="font-mono text-xs" style={{ color: "var(--subtitle-text-color)" }}>{f.ticket.ticket_code}</span></td>
                                        <td style={tdStyle}><span className="text-sm">{f.ticket.title}</span></td>
                                        <td style={tdStyle}><span className="text-sm">{f.ticket.customer_name || "—"}</span></td>
                                        <td style={tdStyle}><span className="text-sm">{f.ticket.assigned_to_name || "—"}</span></td>
                                        <td style={tdStyle}><span className="text-sm">{f.ticket.support_group_name || "—"}</span></td>
                                        <td style={tdStyle}>
                                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: `${sc}1f`, color: sc, fontWeight: 600, fontSize: 12, padding: "2px 10px", borderRadius: 999 }}>
                                                <span style={{ width: 7, height: 7, borderRadius: "50%", background: sc }} />
                                                {t(STATUS_TEXT_KEYS[f.ticket.status as TicketStatus])}
                                            </span>
                                        </td>
                                        <td style={tdStyle}>
                                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: `${f.color}1f`, color: f.color, fontWeight: 600, fontSize: 11, padding: "2px 8px", borderRadius: 999, whiteSpace: "nowrap" }}>
                                                <span style={{ width: 7, height: 7, borderRadius: "50%", background: f.color }} />
                                                {f.label}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

const thStyle: React.CSSProperties = { padding: "10px 14px", fontWeight: 600 };
const tdStyle: React.CSSProperties = { padding: "10px 14px", verticalAlign: "middle" };

function Metric({ label, value, color, subtitle }: { label: string; value: string | number; color: string; subtitle?: string }) {
    return (
        <div style={{ background: "var(--base-bg)", border: "1px solid var(--border-color)", borderLeft: `4px solid ${color}`, borderRadius: 12, padding: "14px 16px" }}>
            <div style={{ fontSize: 28, fontWeight: 700, color }}>{value}</div>
            <div className="text-xs" style={{ color: "var(--subtitle-text-color)", marginTop: 2 }}>{label}</div>
            {subtitle && <div className="text-xs" style={{ color: "var(--subtitle-text-color)", marginTop: 2, opacity: 0.8 }}>{subtitle}</div>}
        </div>
    );
}
